import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Settings, Clock, Mail, Phone, Save, Globe, Bell } from 'lucide-react';

const TABS = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'hours', label: 'Horario', icon: Clock },
  { id: 'email', label: 'Email / SMTP', icon: Mail },
  { id: 'advanced', label: 'Avanzado', icon: Globe },
];

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];

export default function SettingsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('general');
  const { register: regGen, handleSubmit: submitGen } = useForm();
  const { register: regEmail, handleSubmit: submitEmail } = useForm();

  const { data: settings } = useQuery('settings', () => api.get('/settings').then(r => r.data));
  const { data: hours = [] } = useQuery('business-hours', () => api.get('/settings/business-hours').then(r => r.data));
  const { data: holidays = [] } = useQuery('holidays', () => api.get('/settings/holidays').then(r => r.data));

  const saveMut = useMutation(
    (d) => api.put('/settings', d),
    { onSuccess: () => { qc.invalidateQueries('settings'); toast.success('Configuracion guardada'); },
      onError: () => toast.error('Error al guardar') }
  );

  const saveHoursMut = useMutation(
    (d) => api.put('/settings/business-hours', d),
    { onSuccess: () => { qc.invalidateQueries('business-hours'); toast.success('Horario actualizado'); } }
  );

  const [hoursState, setHoursState] = useState(null);
  const currentHours = hoursState || hours;

  const updateHour = (idx, field, value) => {
    const updated = [...(currentHours.length ? currentHours : hours)];
    updated[idx] = { ...updated[idx], [field]: value };
    setHoursState(updated);
  };

  const saveHours = () => {
    const toSave = (hoursState || hours).map(h => ({
      dayOfWeek: h.day_of_week,
      openTime: h.open_time,
      closeTime: h.close_time,
      isOpen: h.is_open,
    }));
    saveHoursMut.mutate(toSave);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Configuracion</h1>
        <p className="text-gray-400 text-sm">Parametros generales del sistema PBX</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ' +
              (tab === t.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200')}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* General */}
      {tab === 'general' && (
        <div className="card max-w-2xl">
          <h3 className="font-semibold text-white mb-4">Informacion del Sistema</h3>
          <form onSubmit={submitGen(d => saveMut.mutate({
            pbx_name: d.pbx_name, pbx_domain: d.pbx_domain,
            pbx_timezone: d.pbx_timezone, pbx_language: d.pbx_language,
            pbx_max_call_duration: d.pbx_max_call_duration,
            pbx_recording_enabled: d.pbx_recording_enabled,
          }))} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="set-pbx-name">Nombre del PBX</label>
                <input id="set-pbx-name" {...regGen('pbx_name')} className="input"
                  defaultValue={settings?.pbx_name?.value || 'PBX Virtual'} />
              </div>
              <div>
                <label className="label" htmlFor="set-pbx-domain">Dominio / IP publica</label>
                <input id="set-pbx-domain" {...regGen('pbx_domain')} className="input"
                  defaultValue={settings?.pbx_domain?.value || ''} placeholder="pbx.empresa.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="set-pbx-timezone">Zona horaria</label>
                <select id="set-pbx-timezone" {...regGen('pbx_timezone')} className="input"
                  defaultValue={settings?.pbx_timezone?.value || 'America/Mexico_City'}>
                  <option value="America/Mexico_City">Mexico City (CST/CDT)</option>
                  <option value="America/New_York">New York (EST/EDT)</option>
                  <option value="America/Los_Angeles">Los Angeles (PST/PDT)</option>
                  <option value="America/Bogota">Bogota (COT)</option>
                  <option value="America/Lima">Lima (PET)</option>
                  <option value="America/Santiago">Santiago (CLT)</option>
                  <option value="America/Buenos_Aires">Buenos Aires (ART)</option>
                  <option value="Europe/Madrid">Madrid (CET)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              <div>
                <label className="label" htmlFor="set-pbx-language">Idioma principal</label>
                <select id="set-pbx-language" {...regGen('pbx_language')} className="input"
                  defaultValue={settings?.pbx_language?.value || 'es'}>
                  <option value="es">Espanol</option>
                  <option value="en">Ingles</option>
                  <option value="pt">Portugues</option>
                  <option value="fr">Frances</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="set-max-call-duration">Duracion maxima de llamada (seg)</label>
                <input id="set-max-call-duration" {...regGen('pbx_max_call_duration')} type="number" className="input"
                  defaultValue={settings?.pbx_max_call_duration?.value ?? 0} min={0} max={86400} />
              </div>
              <div>
                <label className="label" htmlFor="set-recording-enabled">Grabacion automatica</label>
                <select id="set-recording-enabled" {...regGen('pbx_recording_enabled')} className="input"
                  defaultValue={settings?.pbx_recording_enabled?.value || 'false'}>
                  <option value="false">Desactivada</option>
                  <option value="true">Activada (todas las llamadas)</option>
                </select>
              </div>
            </div>
            <button type="submit" className="btn-primary">
              <Save size={16} /> Guardar configuracion
            </button>
          </form>
        </div>
      )}

      {/* Horario */}
      {tab === 'hours' && (
        <div className="card max-w-2xl">
          <h3 className="font-semibold text-white mb-4">Horario Comercial</h3>
          <div className="space-y-2">
            {(currentHours.length ? currentHours : Array(7).fill(null)).map((h, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-3 items-center py-2 border-b border-gray-800 last:border-0">
                <div className="col-span-2 text-sm font-medium text-gray-300">{DAYS[idx]}</div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-blue-600"
                      checked={h?.is_open || false}
                      onChange={e => updateHour(idx, 'is_open', e.target.checked)} />
                    <span className="text-xs text-gray-400">Abierto</span>
                  </label>
                </div>
                <div className="col-span-4">
                  <input type="time" aria-label={`Hora de apertura ${DAYS[idx]}`} className="input text-sm py-1.5"
                    value={h?.open_time || '08:00'}
                    onChange={e => updateHour(idx, 'open_time', e.target.value)}
                    disabled={!h?.is_open} />
                </div>
                <div className="col-span-4">
                  <input type="time" aria-label={`Hora de cierre ${DAYS[idx]}`} className="input text-sm py-1.5"
                    value={h?.close_time || '18:00'}
                    onChange={e => updateHour(idx, 'close_time', e.target.value)}
                    disabled={!h?.is_open} />
                </div>
              </div>
            ))}
          </div>
          <button onClick={saveHours} className="btn-primary mt-4">
            <Save size={16} /> Guardar horario
          </button>
        </div>
      )}

      {/* Email */}
      {tab === 'email' && (
        <div className="card max-w-2xl">
          <h3 className="font-semibold text-white mb-4">Configuracion SMTP</h3>
          <form onSubmit={submitEmail(d => saveMut.mutate({
            smtp_host: d.smtp_host, smtp_port: d.smtp_port, smtp_user: d.smtp_user,
            smtp_pass: d.smtp_pass, smtp_from: d.smtp_from, smtp_tls: d.smtp_tls,
          }))} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="set-smtp-host">Servidor SMTP</label>
                <input id="set-smtp-host" {...regEmail('smtp_host')} className="input"
                  defaultValue={settings?.smtp_host?.value || ''} placeholder="smtp.gmail.com" />
              </div>
              <div>
                <label className="label" htmlFor="set-smtp-port">Puerto</label>
                <input id="set-smtp-port" {...regEmail('smtp_port')} type="number" className="input"
                  defaultValue={settings?.smtp_port?.value || 587} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="set-smtp-user">Usuario SMTP</label>
                <input id="set-smtp-user" {...regEmail('smtp_user')} className="input"
                  defaultValue={settings?.smtp_user?.value || ''} placeholder="pbx@empresa.com" />
              </div>
              <div>
                <label className="label" htmlFor="set-smtp-pass">Contrasena SMTP</label>
                <input id="set-smtp-pass" {...regEmail('smtp_pass')} type="password" className="input" placeholder="••••••••" autoComplete="new-password" />
              </div>
            </div>
            <div>
              <label className="label" htmlFor="set-smtp-from">Email remitente</label>
              <input id="set-smtp-from" {...regEmail('smtp_from')} className="input"
                defaultValue={settings?.smtp_from?.value || ''} placeholder="PBX Virtual <pbx@empresa.com>" />
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input {...regEmail('smtp_tls')} type="checkbox" className="w-4 h-4 accent-blue-600" defaultChecked />
                <span className="text-sm text-gray-300">Usar TLS/STARTTLS</span>
              </label>
            </div>
            <button type="submit" className="btn-primary">
              <Save size={16} /> Guardar SMTP
            </button>
          </form>
        </div>
      )}

      {/* Avanzado */}
      {tab === 'advanced' && (
        <div className="card max-w-2xl">
          <h3 className="font-semibold text-white mb-4">Configuracion Avanzada</h3>
          <div className="space-y-4">
            <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
              <div className="font-medium text-gray-200 mb-1">WebRTC / Softphone</div>
              <div className="text-xs text-gray-400 mb-3">Habilita el softphone integrado en el navegador para llamadas sin instalar software.</div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-blue-600"
                  defaultChecked={settings?.pbx_webrtc_enabled?.value === 'true'}
                  onChange={e => saveMut.mutate({ pbx_webrtc_enabled: String(e.target.checked) })} />
                <span className="text-sm text-gray-300">WebRTC habilitado</span>
              </label>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
              <div className="font-medium text-gray-200 mb-1">Buzon de Voz Global</div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-blue-600"
                  defaultChecked={settings?.pbx_voicemail_enabled?.value !== 'false'}
                  onChange={e => saveMut.mutate({ pbx_voicemail_enabled: String(e.target.checked) })} />
                <span className="text-sm text-gray-300">Buzon de voz habilitado para todas las extensiones</span>
              </label>
            </div>
            <div className="p-4 bg-yellow-900/20 rounded-xl border border-yellow-800/40">
              <div className="font-medium text-yellow-400 mb-1 flex items-center gap-2">
                <Bell size={15} /> Zona de peligro
              </div>
              <p className="text-xs text-gray-400 mb-3">Estas acciones afectan el servicio de telefonia en produccion.</p>
              <div className="flex gap-2">
                <button onClick={() => api.post('/system/asterisk/reload').then(() => toast.success('Asterisk recargado'))}
                  className="btn-secondary text-xs py-1.5">Recargar Asterisk</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
